import { VaultPasswordRow } from '../backup/VaultPasswordRow'
import { Icon } from '../ui/Icon'
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { fetchSession, signOut, type Session } from './googleSession'
import { personBackground, personLetter } from '../identity/personAvatar'
import { ProfilePageLazy as ProfilePage } from './ProfilePageLazy'

/**
 * Who you are signed in as, and the way out.
 *
 * The settings panel used to identify you by the vault's username — a name this app
 * asked you to invent. It is now the address Google verified, which is both truer and
 * the only identity the rest of the system will recognise.
 *
 * SIGNING OUT RELOADS, deliberately. `signOut` ends the session on the server, but
 * this app is full of state fetched while the previous session was live: agent
 * rosters, transcripts, cached queries. Clearing the session without clearing them
 * would leave one account's data on screen under another account's absence — the
 * screen would say "signed out" while still showing what the signed-in user could
 * see. A reload is the only version of "sign out" with no residue.
 */
export function AccountSection() {
  const qc = useQueryClient()
  const [session, setSession] = useState<Session | null>(null)
  const [opened, setOpened] = useState(false)

  useEffect(() => {
    void fetchSession().then(setSession)
  }, [])

  return (
    <>
      <div className="gh-sec">Account</div>
      {session === null ? (
        <p className="gh-note">Checking…</p>
      ) : session.kind === 'in' ? (
        // The identity only. Sign out moved to the FOOT of the panel — owner,
        // 2026-08-14: 把 Sign Out 放到最下面, 移除 "Verified by Google…" 這行 —
        // so the account block answers "who am I" and the way out sits at the
        // bottom where a way out belongs, away from the identity it acts on.
        /* THE WHOLE ROW IS THE DOOR — owner, 2026-10-20: 讓使用者可以點擊他的帳號設定個人頁面. It was the face
           alone, straight into the editor (2026-09-13: 按下使用者頭像的時候彈跳出來一個可以編輯使用者 Profile
           的頁面); now the face, the name and the address all open YOUR PAGE — the one your friends see — and
           Edit profile is on it. A button, so it is reachable by keyboard and announces itself. */
        <button
          type="button"
          className="acct-row acct-open"
          onClick={() => setOpened(true)}
          aria-label="Your profile"
          title="Your profile"
        >
          {/* Google's own avatar when there is one. `picture` is absent for accounts
              that never set a profile — and for EVERY Apple account — and an <img>
              with a null src renders a broken icon. Absence used to get no element;
              it gets the lettered circle now (owner, 2026-09-13: Apple 帳號沒有頭像,
              用 email 首字 + 隨機背景的圓圈代替 — identity/personAvatar.ts). */}
          <span className="acct-face">
            {session.identity.picture ? (
              <img
                className="acct-pic"
                src={session.identity.picture}
                alt=""
                referrerPolicy="no-referrer"
                width={36}
                height={36}
              />
            ) : (
              <span
                className="acct-pic acct-fallback"
                style={{ background: personBackground(session.identity.email) }}
                aria-hidden="true"
              >
                {personLetter(session.identity.email)}
              </span>
            )}
          </span>
          <span className="acct-who">
            {/* `name` is nullable for the same reason. The email is the field that
                always exists, so it is the one that always shows. */}
            {session.identity.name ? (
              <span className="acct-name">{session.identity.name}</span>
            ) : null}
            <span className="acct-mail">{session.identity.email}</span>
          </span>
          <Icon name="chevron_right" size={16} />
        </button>
      ) : null}
      {session?.kind === 'in' && <VaultPasswordRow />}
      {session === null || session.kind === 'in' ? null : session.kind === 'out' ? (
        <p className="gh-note">Not signed in.</p>
      ) : (
        /* Same distinction the gate makes: this is "cannot tell", not "signed out".
           Showing "Not signed in" here would be a confident answer nobody checked. */
        <p className="gh-note">Could not reach sign-in — {session.detail}</p>
      )}
      {opened && session?.kind === 'in' && (
        <ProfilePage
          who={{ self: true }}
          selfEmail={session.identity.email}
          onClose={() => setOpened(false)}
          /* Re-read the session so the row shows the new name and picture at once.
             Other surfaces (a chat roster, a friends list) carry their own copies and
             pick it up on their next fetch — the profile is read per request, not
             cached anywhere but the browser's image cache. */
          onSaved={() => {
            void fetchSession().then(setSession)
            /*
             * AND EVERY OTHER SURFACE THAT CARRIES A COPY — owner, 2026-09-13:
             * 更新了使用者的 avatar 之後, 並不會馬上的反應在 project chat 或是 friends
             * chat 裡面, 需要登出再登入或者重開 app.
             *
             * The comment that used to sit here said those surfaces "pick it up on
             * their next fetch". They do not: `['auth-me']` is `staleTime: Infinity`
             * — it is never refetched, by design, because who you are does not
             * change mid-session — so every consumer of it (the chat surfaces' own
             * name and photo, the rail's face, an optimistic send) held the old
             * picture until the app was reloaded. That is exactly what a profile
             * edit changes, so the edit is the thing that has to say so.
             *
             * The line above updates only THIS component's local state, which is
             * why the account row looked right and nothing else did.
             */
            void qc.invalidateQueries({ queryKey: ['auth-me'] })
            void qc.invalidateQueries({ queryKey: ['friends'] })
            void qc.invalidateQueries({ queryKey: ['proj-members'] })
          }}
        />
      )}
    </>
  )
}

/**
 * The way out, at the right of the account row — owner, 2026-09-13: 把 Sign out 放置
 * 於使用者顯示頭像的那一個欄位的右邊. (It was at the FOOT of the menu from 2026-08-14
 * until then. The later instruction wins; this note keeps the old one from reading
 * like a rule someone broke.)
 *
 * SIGNING OUT RELOADS, deliberately. `signOut` ends the session on the server, but
 * this app is full of state fetched while the previous session was live: agent
 * rosters, transcripts, cached queries. Clearing the session without clearing them
 * would leave one account's data on screen under another account's absence — the
 * screen would say "signed out" while still showing what the signed-in user could
 * see. A reload is the only version of "sign out" with no residue.
 *
 * Rendered by AccountSection inside its signed-in branch, so "only while there is a
 * session to end" is structural. It used to answer that question with a second
 * `/auth/me` of its own, which is gone with the move.
 */
/**
 * THE WAY OUT, IN ITS OWN CARD — owner, 2026-09-30: 使用者可以 sign out 的地方應該也是另外
 * 一個部分. It sat right of the avatar from 2026-09-13 (an earlier instruction); the later one
 * wins. Rendered only while there is a session to end.
 */
export function SignOutSection() {
  const [session, setSession] = useState<Session | null>(null)
  useEffect(() => {
    void fetchSession().then(setSession)
  }, [])
  if (session?.kind !== 'in') return null
  return <SignOutButton />
}

function SignOutButton() {
  const [busy, setBusy] = useState(false)

  const out = async () => {
    setBusy(true)
    try {
      await signOut()
    } catch {
      /*
       * Swallowed, and the reload below happens anyway. The server may well have
       * dropped the session before the response was lost, so treating a failed
       * request as "still signed in" would be a guess in the more dangerous
       * direction. And `out()` is called with `void`, so without this the
       * rejection would escape into the page.
       */
    } finally {
      window.location.replace('/')
    }
  }

  return (
    <button type="button" className="svm-row-btn" onClick={() => void out()} disabled={busy}>
      {busy ? 'Signing out…' : 'Sign out'}
    </button>
  )
}
